'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations } from '@/i18n/useTranslations';
import { Button } from '@/components/ui/button';
import { SendHorizontal, Loader2 } from 'lucide-react';
import { useAiChatStore } from '@/stores/ai-chat-store';

interface AiChatInputProps {
  onSend: (content: string) => void;
}

export function AiChatInput({ onSend }: AiChatInputProps) {
  const t = useTranslations('ai');
  const tCommon = useTranslations('common');
  const [input, setInput] = useState('');
  const isStreaming = useAiChatStore((s) => s.isStreaming);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  const handleSubmit = () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    onSend(trimmed);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-surface p-3">
      {isStreaming && (
        <div className="flex items-center gap-1.5 px-1 pb-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>{t('responding')}</span>
        </div>
      )}
      <div className="flex gap-2 items-end">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isStreaming ? t('responding') : t('inputPlaceholder')}
          rows={1}
          disabled={isStreaming}
          className="flex-1 resize-none rounded-xl bg-overlay/50 border border-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 max-h-[120px] disabled:opacity-60"
        />
        <Button
          size="icon"
          onClick={handleSubmit}
          disabled={!input.trim() || isStreaming}
          className="shrink-0 rounded-xl h-9 w-9"
          aria-label={tCommon('sendMessage')}
        >
          <SendHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
